// File: frontend/src/pages/person/PersonPage.tsx
// src/pages/person/PersonPage.tsx

import React, { useEffect, useState, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { RedirectError } from "../../services/apiClient";
import { usePersonRoles } from "../../hooks/usePersonRoles";
import { RiZoomInLine, RiCloseLine } from "react-icons/ri";
import "./PersonPage.css";
import { PersonModel, CareerEntryModel } from "../../models/PersonModel";
import { getPublicPersonBySlug } from "../../services/adminPersonService";
import { postPersonEntry, fetchEntriesByPersonId } from "../../services/personEntryService";
import { usePersonEntries } from "../../hooks/usePersonEntries";
import { useCollapsedOldEntries, CollapseEntryFetcher } from "../../hooks/useCollapsedOldEntries";
import { useDocumentMeta } from "../../hooks/useDocumentMeta";
import { useUrlPage, pageSuffixes, useIsEntryDeepLink, useFocusEntryId, useExitFocus } from "../../hooks/useUrlPage";
import { useFocusedEntry } from "../../hooks/useFocusedEntry";
import { Pagination } from "../../utils";
import { absoluteUrl, personUrl, withCurrentQueryAndHash } from "../../utils/canonicalUrl";
import { buildTopicShareUrl } from "../../utils/shareUtils";
import { SpinnerLoading } from "../../utils";
import { EntryRow } from "../../components/entries/EntryRow";
import { CollapsedEntriesBar } from "../../components/entries/CollapsedEntriesBar";
// Sitewide pins: admin-pinned entry shown at the top of the topic page
import { PinnedEntryBlock } from "../../components/entries/PinnedEntryBlock";
import { EntryToolbar, EntrySortOption } from "../../components/entries/EntryToolbar";
import { EntryPostForm } from "../../components/entries/EntryPostForm";
import { usePreferences } from "../../context/PreferencesContext";
import { useAuth } from "../../context/AuthContext";
import { ENTRIES_PER_PAGE } from "../../constants";
import {
  FaBirthdayCake,
  FaMapMarkerAlt,
  FaFlag,
  FaUser,
  FaArrowLeft,
} from "react-icons/fa";

interface PersonPageProps {
  /** URL slug from the /kisi/{slug} route, e.g. "fatih-terim" or "fred". */
  personSlug: string;
  // Called when the mobile back button is tapped to return to the list pane
  onMobileBack?: () => void;
}

// Maximum number of simultaneous open-ended roles listed under the person's name
const MAX_CURRENT_ROLES = 3;

const formatYears = (startYear: number, endYear: number | null, currentLabel: string): string =>
  endYear ? `${startYear} – ${endYear}` : `${startYear} – ${currentLabel}`;

/**
 * Builds an optional " Şu an {club} forması giyiyor." suffix for the meta
 * description from the person's current (endYear === null) career entry, if
 * any. Returns an empty string when there is no active career entry — the
 * caller just appends this directly onto the base sentence.
 */
const buildPersonCurrentClubSuffix = (person: PersonModel | null): string => {
  if (!person) return "";
  // A person can hold several open-ended roles at once (e.g. an owner of two clubs),
  // so pick the most recently started one instead of whichever row came back first.
  const current = [...person.careerEntries]
    .filter((c) => c.endYear === null)
    .sort((a, b) => b.startYear - a.startYear || b.id - a.id)[0];
  const clubName = current?.clubShortName || current?.customClubName;
  if (!clubName) return "";
  return ` Şu an ${clubName} forması giyiyor.`;
};

/** Flag asset path — same lipis/flag-icons set served via Nginx as elsewhere in the app
    (mirrors the helper in PaneFilterBar.tsx). Person country codes are ISO 3166-1
    alpha-2, but the UK map is kept so pseudo-codes can never resolve to a wrong flag. */
const UK_NATION_FLAG_CODES: Record<string, string> = {
  ENG: "gb-eng",
  SCO: "gb-sct",
  WAL: "gb-wls",
  NIR: "gb-nir",
};

const flagUrl = (code: string | null): string | null => {
  if (!code) return null;
  const upper = code.toUpperCase();
  const flagCode = UK_NATION_FLAG_CODES[upper] ?? code.slice(0, 2).toLowerCase();
  return `/media/flags/4x3/${flagCode}.svg`;
};

export const PersonPage: React.FC<PersonPageProps> = ({ personSlug, onMobileBack }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { translateRole } = usePersonRoles();
  const { preferences } = usePreferences();
  const { isAuthenticated } = useAuth();

  const [person, setPerson]     = useState<PersonModel | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  // Career section expanded by default — user can collapse it
  const [careerOpen, setCareerOpen] = useState(true);

  // Avatar photo: popover open state, and whether the photo URL 404s/fails
  // to load (stale or wrong link) — in that case we fall back to the
  // placeholder badge instead of a broken-image glyph.
  const [avatarPhotoOpen, setAvatarPhotoOpen] = useState(false);
  const [avatarPhotoFailed, setAvatarPhotoFailed] = useState(false);
  // Screen coordinates the popover renders at — computed from the avatar's
  // own position when opened, so it appears right next to the photo it
  // enlarges instead of dead-center of the screen. Rendered via a portal
  // (see below), so this is relative to the viewport, not this component.
  const avatarBtnRef = useRef<HTMLButtonElement>(null);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null);

  // Mirrors the CSS sizing on .person-page__avatar-popover img — kept in
  // sync manually since the clamp math below needs the same numbers CSS uses.
  const POPOVER_W = 170;
  const POPOVER_H = 210;
  const POPOVER_MARGIN = 16;

  const openAvatarPhoto = () => {
    // Mobile: always center — anchoring next to a tiny header avatar on a
    // narrow screen has nowhere good to go and just looks broken. Desktop
    // keeps the "pop out right next to the photo" behavior.
    const isMobile = window.innerWidth <= 768;
    const rect = isMobile ? undefined : avatarBtnRef.current?.getBoundingClientRect();
    const effW = Math.min(window.innerWidth * 0.55, POPOVER_W);
    const effH = Math.min(window.innerHeight * 0.45, POPOVER_H);
    if (rect) {
      let left = rect.left;
      let top = rect.bottom + 10;
      // Keep it on-screen: slide left if it would overflow the right edge...
      if (left + effW + POPOVER_MARGIN > window.innerWidth) {
        left = Math.max(POPOVER_MARGIN, window.innerWidth - effW - POPOVER_MARGIN);
      }
      // ...and flip above the avatar if there isn't enough room below it.
      if (top + effH + POPOVER_MARGIN > window.innerHeight) {
        top = Math.max(POPOVER_MARGIN, rect.top - effH - 10);
      }
      setPopoverPos({ top, left });
    } else {
      setPopoverPos(null);
    }
    setAvatarPhotoOpen(true);
  };

  // Toolbar state: keyword search, filters, and sort for the entry list
  const [sort, setSort]               = useState<EntrySortOption>("oldest");
  const [filterToday, setFilterToday] = useState(false);
  const [rookieOnly, setRookieOnly]   = useState(false);
  const [keyword, setKeyword]         = useState("");

  // Anchor used by the toolbar "sayfa sonu" button to scroll down to the entry form
  const bottomRef = useRef<HTMLDivElement>(null);
  const handleScrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // ESC closes the avatar photo popover, same convention as other overlays.
  useEffect(() => {
    if (!avatarPhotoOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setAvatarPhotoOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [avatarPhotoOpen]);

  const showRookieEntries = preferences?.showRookieEntries !== false;
  const entriesPerPage    = preferences?.entriesPerPage ?? ENTRIES_PER_PAGE;

  // usePersonEntries needs the numeric person id — resolved after fetching by slug
  const personId = person?.id ?? null;

  // Sets document.title / meta description / canonical / OG tags for this
  // person's page once the entity has loaded. Falls back to the site-wide
  // defaults (see useDocumentMeta) while loading or on load failure, so a
  // slow API response never flashes an empty or wrong title.
  const personDisplayName = person?.knownName || person?.fullName || null;
  // Entry page number lives in the URL ("?sayfa=2") rather than in state, so a
  // page can be linked to and shared, survives a refresh, and gives each page
  // its own indexable address.
  const { page: urlPage, setPage: setUrlPage } = useUrlPage();

  useDocumentMeta({
    title: personDisplayName ? `${personDisplayName}${pageSuffixes(urlPage).titleSuffix} - Üç Korner Bir Penaltı` : undefined,
    description: personDisplayName
      ? `${personDisplayName} hakkında yazılan tanımları oku, sen de yorumunu ekle, tartış ve oyla.${buildPersonCurrentClubSuffix(person)}`
      : undefined,
    canonicalUrl: person ? absoluteUrl(personUrl(person.slug, person.id)) + pageSuffixes(urlPage).urlSuffix : undefined,
    imageUrl: person?.photoUrl || undefined,
  });

  const {
    entries, loading: loadingEntries, error: entriesError,
    totalItems, currentPage, totalPages,
    isLoadingMore, loadEntries, loadMoreEntries, updateEntries,
  } = usePersonEntries(personId, showRookieEntries, sort, entriesPerPage, filterToday, rookieOnly, keyword, urlPage);

  // Only active in the default view: oldest sort, no filters, no keyword.
  // An address that names a page ("?sayfa=4") or an entry ("#entry-1499") is a
  // deep link, and the collapsed view cannot serve it: it renders only today's
  // entries and hides the pagination controls, so the requested page was
  // fetched and never shown and the anchored entry was never in the DOM for
  // the scroll to find. An explicit request wins over the default view.
  const isDeepLink = useIsEntryDeepLink();
  const collapseEligible =
    sort === "oldest" && !filterToday && !rookieOnly && !keyword.trim() && !isDeepLink;

  // Fetcher used by the collapse hook — always oldest sort, viewer's rookie preference
  const collapseFetcher = useCallback<CollapseEntryFetcher>(async (page, size, fToday) => {
    if (personId === null) throw new Error("No person selected");
    const res = await fetchEntriesByPersonId(personId, page, size, showRookieEntries, "oldest", fToday, false, "");
    return { entries: res.entries, totalItems: res.totalItems, totalPages: res.totalPages };
  }, [personId, showRookieEntries]);

  const collapse = useCollapsedOldEntries(personId, collapseEligible, entriesPerPage, collapseFetcher);

  // Permalink view: "?tanim=747" shows that one tanım framed by "{n} entry
  // daha" bars instead of dropping the reader into the middle of a page. It
  // reuses the collapse fetcher, so nothing new has to be wired per domain.
  const focusEntryId = useFocusEntryId();
  const exitFocus = useExitFocus();
  const focus = useFocusedEntry(personId, focusEntryId, entriesPerPage, collapseFetcher);
  const handleExitFocus = useCallback(() => {
    if (focus.entry) exitFocus(focus.page, focus.entry.id);
  }, [exitFocus, focus.entry, focus.page]);

  // Entries actually rendered: collapsed view (today's + revealed older) or the normal list
  // Focused permalink view wins over both the collapsed view and the normal
  // list: the reader asked for one specific tanım.
  const displayEntries = focus.active
    ? (focus.entry ? [focus.entry] : [])
    : (collapse.active ? collapse.entries : entries);

  // Apply local mutations (vote, delete) to both lists so the visible one stays correct
  const applyEntriesUpdate = useCallback((fn: (prev: typeof entries) => typeof entries) => {
    updateEntries(fn);
    collapse.updateEntries(fn);
  }, [updateEntries, collapse.updateEntries]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Ticket 1c: the header (name, photo, roles, share link) comes from this
    // request while the entry list comes from a second, independent one. Two
    // quick clicks in the left pane leave two person requests in flight, and
    // without this flag the slower one wins simply by arriving last — which is
    // how the page ended up showing one person's header above another person's
    // entries. React runs the cleanup before re-running the effect, so the
    // superseded request is marked stale the instant a new slug is requested.
    let cancelled = false;

    setLoading(true);
    setError(null);
    setPerson(null);
    // New person — a stale "photo failed to load" flag or an open photo
    // popover from the previous person must not leak into this one.
    setAvatarPhotoFailed(false);
    setAvatarPhotoOpen(false);
    setPopoverPos(null);
    getPublicPersonBySlug(personSlug)
      .then((loaded) => {
        if (cancelled) return;
        setPerson(loaded);
      })
      .catch((err) => {
        if (cancelled) return;
        // The person topic was merged — navigate to the surviving person page
        // Covers both a merged-away topic (410) and a non-canonical
        // slug (301) — both mean "continue at redirectPath".
        if (err instanceof RedirectError) {
          // Keep "?sayfa=" and "#entry-": this fires for every legacy or
          // non-canonical slug, which is exactly what an older shared deep
          // link is. Passing the path alone dropped the page and the anchor,
          // so the reader landed on page 1 with nothing to scroll to.
          navigate(withCurrentQueryAndHash(err.redirectPath), { replace: true });
          return;
        }
        setError(t("person_load_error", "Kişi bilgileri yüklenemedi."));
      })
      .finally(() => {
        // Ticket 1c: only the request that is still current may clear the
        // spinner — otherwise the newer person renders with stale header state.
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [personSlug, t, navigate]);

  const handlePostEntry = useCallback(async (content: string) => {
    if (!personId) return;
    const newEntry = await postPersonEntry(content, personId);
    updateEntries(prev => [newEntry, ...prev]);
    // Collapsed view is chronological (oldest first) — the new entry goes to the end
    collapse.updateEntries(prev => [...prev, newEntry]);
    loadEntries(1);
  }, [personId, updateEntries, collapse.updateEntries, loadEntries]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteEntry = (entryId: number) => {
    applyEntriesUpdate(prev => prev.filter(e => e.id !== entryId));
  };

  if (loading) return <SpinnerLoading />;
  if (error)   return <div className="person-page__error">{error}</div>;
  if (!person) return <div className="person-page__not-found">{t("person_not_found", "Kişi bulunamadı.")}</div>;

  const apiBase = process.env.REACT_APP_API_URL || "http://localhost:8080";

  // Resolved photo URL, and whether it's actually usable right now — either
  // there was never a photoUrl, or the <img> below fired onError (dead link).
  const photoUrl = person.photoUrl ? `${apiBase}${person.photoUrl}` : null;
  const hasPhoto = Boolean(photoUrl) && !avatarPhotoFailed;

  const birthDateStr = person.birthDate
    ? new Date(person.birthDate).toLocaleDateString(
        i18n.language.startsWith("tr") ? "tr-TR" : "en-US",
        { day: "numeric", month: "long", year: "numeric" }
      )
    : null;

  // Calculate age in full years from birthDate to today
  const birthAge = person.birthDate
    ? (() => {
        const today = new Date();
        const born  = new Date(person.birthDate);
        let age = today.getFullYear() - born.getFullYear();
        const hasHadBirthdayThisYear =
          today.getMonth() > born.getMonth() ||
          (today.getMonth() === born.getMonth() && today.getDate() >= born.getDate());
        if (!hasHadBirthdayThisYear) age -= 1;
        return age;
      })()
    : null;

  const translateCountry = (code: string | null): string | null => {
    if (!code) return null;
    const key = `country_${code.toUpperCase()}`;
    const translated = t(key);
    return translated !== key ? translated : code;
  };

  // Birth place rendered as a single value: "city, country". Either part may be
  // missing, in which case only the available one is shown.
  const birthCountryName = translateCountry(person.birthCountryCode);
  const birthPlaceStr =
    [person.birthCity, birthCountryName].filter(Boolean).join(", ") || null;

  // Nationality is only rendered when it differs from the birth country —
  // otherwise the birth place line already carries the very same country name.
  const nationalityStr =
    person.nationality && person.nationality !== person.birthCountryCode
      ? translateCountry(person.nationality)
      : null;

  // Whether the meta block has anything to show at all
  const hasMeta = Boolean(birthDateStr || birthPlaceStr || nationalityStr);

  // Real country flags shown next to the country names
  const birthCountryFlag = flagUrl(person.birthCountryCode);
  const nationalityFlag  = nationalityStr ? flagUrl(person.nationality) : null;

  const translateSport = (sportType: string): string => {
    const key = `menu_sport_${sportType.toLowerCase()}`;
    const translated = t(key);
    return translated !== key ? translated : sportType;
  };

  const currentLabel = i18n.language.startsWith("tr") ? "güncel" : "present";

  // Open-ended stints first, then newest start year. The id tiebreak keeps the order
  // stable when two entries share a start year — without it the "current" line could
  // change between renders for people holding two open-ended roles at once.
  const sortedEntries = [...(person.careerEntries ?? [])].sort((a, b) => {
    if (a.endYear === null && b.endYear !== null) return -1;
    if (a.endYear !== null && b.endYear === null) return 1;
    return b.startYear - a.startYear || b.id - a.id;
  });

  // Display name: knownName if available, otherwise fullName
  const displayName = person.knownName ?? person.fullName;

  return (
    <div className="person-page">
      {/* Mobile back button — hidden on desktop via CSS, visible on mobile */}
      {onMobileBack && (
        <button className="mobile-back-btn" onClick={onMobileBack} aria-label="Geri">
          <FaArrowLeft size={14} />
          {t("mobile_back", "Geri")}
        </button>
      )}

      {/* Compact header: avatar on left, info on right, in the same flex row */}
      <div className="person-page__header">
        <div className="person-page__avatar-wrapper">
          {hasPhoto ? (
            <button
              ref={avatarBtnRef}
              type="button"
              className="person-page__avatar person-page__avatar--photo"
              onClick={openAvatarPhoto}
              title={t("person_view_photo", "Fotoğrafı büyüt")}
              aria-label={t("person_view_photo", "Fotoğrafı büyüt")}
            >
              <img
                src={photoUrl as string}
                alt={displayName}
                onError={() => setAvatarPhotoFailed(true)}
              />
              {/* Hover/focus affordance: dim overlay + magnifier, same
                  convention as the entry image lightbox trigger. */}
              <span className="person-page__avatar-overlay" aria-hidden="true">
                <RiZoomInLine />
              </span>
            </button>
          ) : (
            // No photo, or the photoUrl is stale/wrong and failed to load —
            // a themed gradient badge with a silhouette icon instead of a
            // broken-image glyph and alt text.
            <div className="person-page__avatar person-page__avatar--placeholder">
              <FaUser size={32} className="person-page__avatar-placeholder-icon" />
            </div>
          )}
        </div>

        {avatarPhotoOpen && hasPhoto && ReactDOM.createPortal(
          // Deliberately NOT the full-screen ImageLightbox used for entry
          // uploads — a profile headshot only needs a modest zoom-in next to
          // the avatar it belongs to, not a full takeover of the screen.
          //
          // Portalled to document.body (same pattern as ImageLightbox /
          // ConfirmModal): rendering this inline would make `position: fixed`
          // resolve against whatever ancestor happens to have a `transform`
          // on it (route-transition wrappers, etc.) instead of the real
          // viewport, which is what was pushing it into a random corner.
          <div
            className="person-page__avatar-popover-backdrop"
            onClick={() => setAvatarPhotoOpen(false)}
          >
            <div
              className="person-page__avatar-popover"
              // Falls back to the CSS default (viewport-centered) if we
              // somehow don't have a measured position yet.
              style={
                popoverPos
                  ? { top: popoverPos.top, left: popoverPos.left, transform: "none" }
                  : undefined
              }
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="person-page__avatar-popover-close"
                onClick={() => setAvatarPhotoOpen(false)}
                aria-label={t("close", "Kapat")}
              >
                <RiCloseLine />
              </button>
              <img src={photoUrl as string} alt={displayName} />
            </div>
          </div>,
          document.body
        )}

        <div className="person-page__header-info">
          <h1 className="person-page__name">{displayName}</h1>
          {person.knownName && person.knownName !== person.fullName && (
            <p className="person-page__official-name">{person.fullName}</p>
          )}

          {/* Current roles displayed directly under the name.
              Every open-ended stint is shown, because someone can genuinely hold more
              than one at the same time (e.g. owning two clubs). Only when there is no
              open-ended stint at all do we fall back to the most recent finished one. */}
          {(() => {
            const openEntries = sortedEntries.filter(e => e.endYear === null);
            const shown = openEntries.length > 0
              ? openEntries.slice(0, MAX_CURRENT_ROLES)
              : sortedEntries.slice(0, 1);
            if (shown.length === 0) return null;
            const hiddenCount = openEntries.length > MAX_CURRENT_ROLES
              ? openEntries.length - MAX_CURRENT_ROLES
              : 0;
            const label = shown
              .map(e => {
                const role = translateRole(e.role);
                const club = e.clubShortName ?? e.customClubName ?? null;
                return club ? `${role} · ${club}` : role;
              })
              .join("  •  ");
            return (
              <p className="person-page__current-role">
                {label}{hiddenCount > 0 ? `  +${hiddenCount}` : ""}
              </p>
            );
          })()}

          {/* Meta block: one labelled row per field, stacked vertically. Birth
              country is merged into the birth place value, and both the place
              and the nationality carry an explicit key so they can never be
              read as the same thing */}
          {hasMeta && (
            <div className="person-page__meta">
              {/* Birth date + age */}
              {birthDateStr && (
                <span className="person-page__meta-item">
                  <FaBirthdayCake size={12} />
                  {birthDateStr}
                  {birthAge !== null && (
                    <span className="person-page__age">({birthAge})</span>
                  )}
                </span>
              )}
              {/* Birth city and birth country as a single value, led by the country flag */}
              {birthPlaceStr && (
                <span className="person-page__meta-item">
                  {birthCountryFlag ? (
                    <img
                      className="person-page__meta-flag"
                      src={birthCountryFlag}
                      alt={birthCountryName ?? ""}
                    />
                  ) : (
                    <FaMapMarkerAlt size={12} />
                  )}
                  <span className="person-page__meta-key">
                    {t("person_meta_birthplace", "Doğum yeri")}:
                  </span>
                  {birthPlaceStr}
                </span>
              )}
              {/* Nationality — inline key prefix keeps it distinct from the birth country */}
              {nationalityStr && (
                <span className="person-page__meta-item">
                  {nationalityFlag ? (
                    <img
                      className="person-page__meta-flag"
                      src={nationalityFlag}
                      alt={nationalityStr}
                    />
                  ) : (
                    <FaFlag size={11} />
                  )}
                  <span className="person-page__meta-key">
                    {t("person_meta_nationality", "Uyruk")}:
                  </span>
                  {nationalityStr}
                </span>
              )}
            </div>
          )}

          {person.bio && <p className="person-page__bio">{person.bio}</p>}
        </div>
      </div>

      {/* Career section — collapsible accordion */}
      {/* Career section — hidden entirely when no entries exist */}
      {sortedEntries.length > 0 && (
        <div className="person-page__section">
          <button
            className="person-page__section-toggle"
            onClick={() => setCareerOpen(o => !o)}
            aria-expanded={careerOpen}
          >
            <h2 className="person-page__section-title">{t("person_career_history", "Kariyer")}</h2>
            <span className="person-page__section-arrow">{careerOpen ? "▼" : "▶"}</span>
          </button>

          {careerOpen && (
            <div className="person-page__career-list">
              {sortedEntries.map(entry => (
                <CareerEntryRow
                  key={entry.id}
                  entry={entry}
                  apiBase={apiBase}
                  translateSport={translateSport}
                  currentLabel={currentLabel}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Toolbar: search, filters, sort, scroll-to-bottom — same behaviour as match pages */}
      <EntryToolbar
        sort={sort}
        onSortChange={setSort}
        filterToday={filterToday}
        onFilterTodayChange={setFilterToday}
        rookieOnly={rookieOnly}
        onRookieOnlyChange={setRookieOnly}
        keyword={keyword}
        onKeywordChange={setKeyword}
        onScrollToBottom={handleScrollToBottom}
        shareUrl={person ? buildTopicShareUrl(personUrl(person.slug, person.id)) : undefined}
        shareTitle={displayName}
      />

      {/* Entries section */}
      <div className="person-page__section">
        <h2 className="person-page__section-title">
          {t("person_entries", "Entryler")}
          {totalItems > 0 && <span className="person-page__entry-count">{totalItems}</span>}
        </h2>

        {focus.loading || collapse.checking || (loadingEntries && !collapse.active && !focus.active) ? (
          <SpinnerLoading />
        ) : entriesError && !collapse.active ? (
          <p className="person-page__empty" style={{ color: "var(--color-error)" }}>{entriesError}</p>
        ) : displayEntries.length === 0 ? (
          <p className="person-page__empty">{t("person_no_entries", "Henüz entry yok.")}</p>
        ) : (
          <>
            {/* Sözlük-convention bar: older entries are collapsed, clicking reveals the previous chunk */}
            {/* Permalink view: the shared tanım is framed by a "{n} entry daha"
                bar on each side. Clicking either one leaves the focused view
                for the full page this entry sits on, with the entry still on
                screen — the bars are the way back into the topic. */}
            {focus.active && (
              <CollapsedEntriesBar
                hiddenCount={focus.hiddenAbove}
                isLoading={false}
                onClick={handleExitFocus}
              />
            )}
            {collapse.active && (
              <CollapsedEntriesBar
                hiddenCount={collapse.hiddenCount}
                isLoading={collapse.isLoadingOlder}
                onClick={collapse.loadOlderEntries}
              />
            )}
            {/* Admin-pinned entry of this topic — rendered above the regular list */}
            <PinnedEntryBlock type="PERSON" targetId={personId} />
            {displayEntries.map(entry => (
              <EntryRow
                key={entry.id}
                entry={entry}
                onDelete={handleDeleteEntry}
                onVoteUpdate={(entryId, updates) =>
                  applyEntriesUpdate(prev => prev.map(e => e.id === entryId ? { ...e, ...updates } : e))
                }
              />
            ))}
            {focus.active && (
              <CollapsedEntriesBar
                hiddenCount={focus.hiddenBelow}
                isLoading={false}
                onClick={handleExitFocus}
              />
            )}

            {/* Page navigation. Reads and writes the "?sayfa=" URL parameter, so the
                reader can link to a page and the back button returns to it. */}
            {!focus.active && totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                paginate={setUrlPage}
              />
            )}
            {!focus.active && !collapse.active && currentPage < totalPages && (
              <button
                className="person-page__load-more"
                onClick={() => loadMoreEntries()}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? t("load_more_loading", "yükleniyor...") : t("load_more_entries", "{{count}} entry daha", { count: totalItems - entries.length })}
              </button>
            )}
          </>
        )}
      </div>

      {/* Entry post form — only shown to authenticated users, same as match pages */}
      {/* Anchor for the toolbar "sayfa sonu" scroll target */}
      <div ref={bottomRef} />

      {isAuthenticated ? (
        <EntryPostForm
          selectedMatch={null}
          personId={personId}
          onPostEntry={handlePostEntry}
          isLocked={person?.isLocked ?? false}
          lockReason={person?.lockReason}
        />
      ) : (
        <p className="login-prompt-message">{t("login_to_post_entry")}</p>
      )}
    </div>
  );
};

/* ── Career entry row ────────────────────────────────────── */

interface CareerEntryRowProps {
  entry: CareerEntryModel;
  apiBase: string;
  translateSport: (s: string) => string;
  currentLabel: string;
}

const CareerEntryRow: React.FC<CareerEntryRowProps> = ({
  entry, apiBase, translateSport, currentLabel,
}) => {
  const { t } = useTranslation();
  const { translateRole } = usePersonRoles();

  const isActive = entry.endYear === null;
  // Roles without any affiliation (referees without a federation, ...) have neither a club
  // nor an organization name — showing the role there reads better than a bare dash.
  const clubName = entry.clubShortName ?? entry.customClubName ?? translateRole(entry.role);

  // Only stints linked to a real club record can be navigated to — free-text
  // affiliations (national teams, organizations) have no club page.
  const clubHref = entry.clubSlug ? `/kulup/${entry.clubSlug}` : null;

  const affiliation = (
    <>
      {entry.clubLogoUrl ? (
        <img className="person-page__career-club-logo" src={`${apiBase}${entry.clubLogoUrl}`} alt={clubName} />
      ) : (
        <div className="person-page__career-club-initials">{clubName.charAt(0).toUpperCase()}</div>
      )}
      <span className="person-page__career-club">{clubName}</span>
    </>
  );

  return (
    <div className={`person-page__career-entry ${isActive ? "person-page__career-entry--active" : ""}`}>
      {clubHref ? (
        <Link to={clubHref} className="person-page__career-club-link">
          {affiliation}
        </Link>
      ) : (
        affiliation
      )}
      <span className="person-page__career-role">
        {translateRole(entry.role)} · {translateSport(entry.sportType)}
      </span>
      <span className="person-page__career-years">
        {formatYears(entry.startYear, entry.endYear, currentLabel)}
      </span>
      {isActive && (
        <span className="person-page__career-badge">{t("person_career_active", "Aktif")}</span>
      )}
    </div>
  );
};